// Static bootstrap for the institution this app instance serves. Imported by
// BOTH the backend (which uses the URLs to reach the Pod) and the browser
// (which uses the labels to render). Contains no secrets — the institutional
// client credentials live only in the backend's environment.
//
// Partners are NOT here: the partner registry lives in the Pod
// (config/solid-data-app/partners.json) and reaches the browser via /api/*.
// Bootstrap is only the CSS URL, this institution, namespaces, and roles.

import { BUILT_IN_PREFIXES } from '../../lib/prefixes.js';

const env = globalThis.process?.env ?? {};
export const CSS_URL = env.CSS_PUBLIC_URL ?? env.CSS_URL ?? 'http://localhost:3000';

// The institution this app instance acts as. One institution, one institutional
// WebID — the identity every partner grants access to. Staff sign in to this
// instance with their own logins (lib/accounts.js), which hold roles (see
// ROLES) — an org-internal concern, not a Solid identity.
//
// This is only what the app needs to *reach* the Pod (slug, WebID, containers).
// The institution's presentation identity — name, label, tagline, brand colour —
// is Pod-resident (config/solid-data-app/institution.json), reaching the browser
// via GET /api/context and the backend via readInstitution(). Same identity, one
// source, and it lives in the Pod (Phase C5).
const institution = env.SOLIDSHARE_ACTIVE_INSTITUTION ?? '';
const podName = env.SOLIDSHARE_ACTIVE_POD ?? institution;
const podBase = podName ? `${CSS_URL}/${podName}/` : '';

// On the server these values come from the ignored runtime environment. In the
// browser the object starts empty and applyContext() fills it from /api/context;
// credentials never enter this shared module or the frontend bundle.
export const SELF = {
  institution,
  webId: podBase ? `${podBase}profile/card#me` : '',
  podBase,
  records: podBase ? `${podBase}diversity/` : '',
  aggregates: podBase ? `${podBase}diversity/aggregates/` : '',
};

export function configureSelfFromEnv(source = globalThis.process?.env ?? {}) {
  const active = source.SOLIDSHARE_ACTIVE_INSTITUTION ?? '';
  const activePod = source.SOLIDSHARE_ACTIVE_POD ?? active;
  const base = activePod ? `${source.CSS_PUBLIC_URL ?? source.CSS_URL ?? CSS_URL}/${activePod}/` : '';
  Object.assign(SELF, {
    institution: active,
    webId: base ? `${base}profile/card#me` : '',
    podBase: base,
    records: base ? `${base}diversity/` : '',
    aggregates: base ? `${base}diversity/aggregates/` : '',
  });
  return SELF;
}

// Roles are bundles of permissions. A login holds one or more roles, and sees
// every screen its roles reach — there is no switching between roles. In this
// phase each institution has one login holding all three; the split exists so
// that a separate analyst (or manager) later is a change to a login, not to the
// app. What each permission allows in the backend is in web/server/policy.js.
export const PERMISSIONS = {
  publish: 'Share cubes with partners (writes WAC rules)',
  ingest: 'Import sources, run ingest and rebuild statistics',
  map: 'Map and label source data',
  'author-cubes': 'Define and delete cubes',
  'author-queries': 'Save and delete comparisons',
};

export const ROLES = {
  manager: {
    id: 'manager',
    label: 'Manager',
    blurb: 'Add partners and decide which cubes each of them receives.',
    permissions: ['publish'],
    screens: ['partners', 'partner', 'partner/access', 'partner/confirm', 'catalog', 'advanced', 'schemas'],
  },
  steward: {
    id: 'steward',
    label: 'Data steward',
    blurb: 'Import sources, ingest them, and map and label their columns.',
    permissions: ['ingest', 'map'],
    screens: ['sources', 'source/mapping', 'source/ingest', 'dataset/new', 'records', 'catalog',
      'advanced', 'schemas'],
  },
  analyst: {
    id: 'analyst',
    label: 'Analyst',
    blurb: 'Build cubes, browse records, and compare with partners.',
    permissions: ['author-cubes', 'author-queries'],
    screens: ['records', 'catalog', 'catalog/new', 'queries', 'analysis', 'advanced', 'schemas'],
  },
};

export const permissionsOf = (roles = []) =>
  new Set(roles.flatMap((role) => ROLES[role]?.permissions ?? []));

export const screensOf = (roles = []) =>
  new Set(roles.flatMap((role) => ROLES[role]?.screens ?? []));

// Namespaces the code itself writes and queries with (lib/prefixes.js). Terms
// from an institution's own vocabulary resolve through its Pod catalog instead.
export const NS = BUILT_IN_PREFIXES;
